import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HiddenContentChip } from 'components';
import { LoadingState } from 'components/ui';
import { getCssPx } from 'components/ui/utilities/zoom';
import ContactAdminOrInquireLicensing from 'pages/Auth/components/ContactAdminOrInquireLicensing';
import ListHeader from 'pages/Auth/components/ListHeader';
import { LIST_HEADER_SEARCH_COMPANIES_LABEL } from 'pages/Auth/components/ListHeader/ListHeader.constants';
import { useAuthInitialFocus, useAuthSelectorKeyboardNavigation } from 'pages/Auth/hooks';
import { COMPANY_SELECTOR_LOADING_LABEL, FILTER_COMPANY_NUMBER } from './CompanySelector.constants';
import type { CompanySelectorPermission, CompanySelectorProps } from './CompanySelector.props';
import { CompaniesListContainer, RootPaper } from './CompanySelector.styles';
import { CompaniesList } from './components';

const CompanySelector = ({ companies, isLoading = false }: CompanySelectorProps) => {
  const [hiddenBottom, setHiddenBottom] = useState(0);
  const [searchCompany, setSearchCompany] = useState('');
  const resolvedCompanies = useMemo(
    () =>
      (companies ?? []).filter(
        (company): company is CompanySelectorPermission => Boolean(company.id != null && company.slug)
      ),
    [companies]
  );

  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { focusFirstOption, handleOptionsKeyDown, handleSearchKeyDown } = useAuthSelectorKeyboardNavigation({
    inputRef,
    listRef,
  });
  const shouldShowSearch = resolvedCompanies.length > FILTER_COMPANY_NUMBER;
  const filteredCompaniesList = useMemo(() => {
    const normalizedSearch = searchCompany.toLocaleLowerCase().trim();

    if (!normalizedSearch) {
      return resolvedCompanies;
    }

    return resolvedCompanies.filter(company => company.name.toLocaleLowerCase().trim().includes(normalizedSearch));
  }, [resolvedCompanies, searchCompany]);

  const updateHiddenCompanies = useCallback(() => {
    const companiesListContainer = listRef.current;

    if (!companiesListContainer) {
      return;
    }

    const companiesListItems = companiesListContainer.querySelectorAll('a');
    let nextHiddenBottom = 0;

    companiesListItems.forEach(company => {
      if (company.offsetTop >= companiesListContainer.offsetHeight + companiesListContainer.scrollTop) {
        nextHiddenBottom += 1;
      }
    });

    setHiddenBottom(currentHiddenBottom =>
      currentHiddenBottom === nextHiddenBottom ? currentHiddenBottom : nextHiddenBottom
    );
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    updateHiddenCompanies();
  }, [filteredCompaniesList, isLoading, updateHiddenCompanies]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchCompany(event.target.value || '');
  };

  const focusSearchInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  useAuthInitialFocus(focusSearchInput, !isLoading && shouldShowSearch);
  useAuthInitialFocus(focusFirstOption, !isLoading && !shouldShowSearch && filteredCompaniesList.length > 0);

  let content;

  if (isLoading) {
    content = <LoadingState label={COMPANY_SELECTOR_LOADING_LABEL} minHeight={getCssPx(220)} />;
  } else if (resolvedCompanies.length === 0) {
    content = <ContactAdminOrInquireLicensing />;
  } else {
    content = (
      <>
        <ListHeader
          inputId="company-selector-search"
          inputFilterRef={inputRef}
          itemCount={resolvedCompanies.length}
          listLimit={FILTER_COMPANY_NUMBER}
          onInputChange={handleInputChange}
          onInputKeyDown={handleSearchKeyDown}
          searchLabel={LIST_HEADER_SEARCH_COMPANIES_LABEL}
          searchState={searchCompany}
        />
        <CompaniesListContainer
          id="companiesList"
          onKeyDown={handleOptionsKeyDown}
          ref={listRef}
          onScroll={updateHiddenCompanies}
          sx={{ maxHeight: 'calc(100vh - 150px)' }}
        >
          <CompaniesList filteredCompaniesList={filteredCompaniesList} />
        </CompaniesListContainer>
        <HiddenContentChip id="hidden-companies" isVisible={hiddenBottom > 0} label={`${hiddenBottom} companies`} position="bottom" />
      </>
    );
  }

  return <RootPaper>{content}</RootPaper>;
};

export default CompanySelector;
